import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import Chart from "pro-features/components/Chart/src/Chart";

import { MarketingChannelsLoader } from "../common/MarketingChannelsLoader";
import { MarketingChannelsNoData } from "../common/MarketingChannelsNoData";
import { useMarketingChannelsServices } from "../common/useMarketingChannelsServices";
import { resolveCurrentGranularity } from "../common/utils/marketingChannelsApiHelper";
import type { ApiOvertimeData } from "../TrafficShareBarChart/types";

import { GraphLegend } from "./components/GraphLegend";
import { ContentContainer, LoaderContainer } from "./TrafficVolumesLineChart.styles";
import { getChartConfig } from "./utils/chartConfig";
import { adaptApiData, sortChartData } from "./utils/chartDataAdapter";
import { useChartLegend } from "./utils/useTrafficVolumesChartLegend";

interface ITrafficVolumesLineChart {
    isLoading: boolean;
    apiData: ApiOvertimeData;
}

export const TrafficVolumesLineChart = ({ isLoading, apiData }: ITrafficVolumesLineChart) => {
    const { services } = useMarketingChannelsServices();
    const { translate } = useTranslate();
    const data = useMemo(() => {
        if (!apiData) {
            return;
        }
        const adaptedData = adaptApiData(apiData, services);
        const sortedData = sortChartData(adaptedData);
        return sortedData;
    }, [apiData, isLoading, services]);
    const { legendItems, handleLegendClick, hiddenSeries } = useChartLegend(data);

    const chartData = useMemo(() => {
        return data?.filter((record) => {
            const isVisible = hiddenSeries.every((series) => series !== record.name);
            return isVisible;
        });
    }, [data, hiddenSeries]);

    const chartConfig = useMemo(() => {
        const timeGranularity = resolveCurrentGranularity(services);
        return getChartConfig(timeGranularity, translate);
    }, [data, services, translate]);

    const GraphContent = useMemo(() => {
        if (isLoading) {
            return (
                <LoaderContainer>
                    <MarketingChannelsLoader />
                </LoaderContainer>
            );
        }
        return (
            <>
                <GraphLegend
                    legendItems={legendItems}
                    onLegendClick={handleLegendClick}
                />
                {!chartData || chartData.length <= 0 ? (
                    <MarketingChannelsNoData services={services} />
                ) : (
                    <Chart
                        type="line"
                        data={chartData}
                        domProps={{ style: { height: "366px" } }}
                        config={chartConfig}
                    />
                )}
            </>
        );
    }, [chartData, chartConfig, handleLegendClick, isLoading, legendItems, services]);

    return <ContentContainer>{GraphContent}</ContentContainer>;
};
